import type { RiskFlowAlert } from "../../lib/riskflow-feed";
import type { RiskFlowItem } from "../../types/api";

type ExtendedRiskFlowItem = RiskFlowItem & {
  image_url?: string | null;
  videoUrl?: string | null;
  tags?: string[];
  promotedAt?: string | null;
  narrativeThreads?: string[];
  status?: RiskFlowAlert["status"];
  marketImpact?: RiskFlowAlert["marketImpact"];
};

export function backendItemToRiskFlowAlert(
  item: RiskFlowItem,
  activeInstrument: string,
): RiskFlowAlert {
  const extended = item as ExtendedRiskFlowItem;
  const publishedAt =
    typeof item.publishedAt === "string"
      ? item.publishedAt
      : item.publishedAt.toISOString();
  const imageUrl =
    item.imageUrl ??
    extended.image_url ??
    item.riskSignalImageUrl ??
    item.risk_signal_image_url ??
    null;

  return {
    id: `backend-${item.id}`,
    headline: item.title,
    summary: item.summary || item.content || "",
    url: item.url,
    imageUrl,
    publishedAt,
    source: mapBackendSource(item.source),
    severity: macroLevelToSeverity(item.macroLevel ?? 0),
    symbols: item.symbols ?? [],
    tags: extended.tags ?? [],
    isBreaking: item.isBreaking ?? false,
    pointRange: item.priceBrainScore?.impliedPoints ?? null,
    direction: item.priceBrainScore?.sentiment ?? null,
    cyclical: item.priceBrainScore?.classification ?? null,
    instrument: activeInstrument,
    authorHandle: item.authorHandle ?? null,
    ivScore: item.ivScore ?? null,
    subScores: item.subScores ?? null,
    headlineWeight: item.headlineWeight ?? null,
    headlineIvScore: item.headlineIvScore ?? null,
    headlineEventWeight: item.headlineEventWeight ?? null,
    headlineFreshness: item.headlineFreshness ?? null,
    headlineImpactWeight: item.headlineImpactWeight ?? null,
    headlineWeightSource: item.headlineWeightSource,
    vixAtPrint: item.vixAtPrint ?? item.subScores?.vixAtPrint ?? null,
    riskType: (item.riskType as RiskFlowAlert["riskType"]) ?? null,
    agentNote: item.agentNote ?? null,
    agentNoteGeneratedAt: item.agentNoteGeneratedAt ?? null,
    videoUrl:
      extended.videoUrl ??
      item.video_url ??
      (item.url && /youtube\.com|youtu\.be/.test(item.url) ? item.url : null),
    econData: item.econData ?? null,
    promotedAt: extended.promotedAt ?? null,
    narrativeThreads: extended.narrativeThreads ?? [],
    category: item.category ?? null,
    status: extended.status ?? null,
    marketImpact: extended.marketImpact ?? item.marketImpact ?? null,
  };
}

function macroLevelToSeverity(level: number): RiskFlowAlert["severity"] {
  if (level >= 4) return "critical";
  if (level >= 3) return "high";
  if (level >= 2) return "medium";
  return "low";
}

function mapBackendSource(source: string): RiskFlowAlert["source"] {
  const normalized = source.toLowerCase();
  if (normalized === "financialjuice") return "financial-juice";
  if (
    normalized === "osintsources" ||
    normalized === "osint613-rss" ||
    normalized === "osint613"
  )
    return "osint-sources";
  if (normalized === "economiccalendar") return "economic-calendar";
  if (normalized === "polymarket") return "polymarket";
  if (normalized === "kalshi") return "kalshi-whale";
  if (normalized === "twittercli" || normalized === "x-home-timeline")
    return "x-home-timeline";
  return "backend";
}
